import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import { fetchApiRef, useApi } from '@backstage/frontend-plugin-api';

// Three independent identities, fetched at runtime from
// /api/silk-infra/version. They are shown separately on purpose: a catalog
// change needs no image rebuild, and an image rebuild does not imply a
// deployment, so a single combined "version" cannot answer "is what I am
// looking at the thing I just changed?".
//
//   build      the container image  (changes when the image is rebuilt)
//   deployment the ACA revision     (changes when a deploy happens)
//   catalog    ingested-content digest (changes when catalog YAML is ingested)

type VersionResponse = {
  build?: string;
  deployment?: string | null;
  catalog?: { digest: string; entities: number } | null;
  // Older shape, kept so a stale bundle still renders something.
  version?: string;
  catalogVersion?: string | null;
};

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 8px 20px',
    gap: 10,
  },
  poweredBy: {
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.7)',
    fontWeight: 600,
  },
  silkLogo: {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  },
  silkLogoSmall: {
    width: 44,
    height: 44,
    objectFit: 'contain',
    display: 'block',
  },
  version: {
    fontFamily: 'monospace',
    fontSize: 10,
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.05em',
    marginTop: 8,
    textAlign: 'center',
    wordBreak: 'break-all',
  },
});

export const SilkAttribution = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const fetchApi = useApi(fetchApiRef);
  const [build, setBuild] = useState<string>('unknown');
  const [deployment, setDeployment] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<string | null>(null);

  useEffect(() => {
    fetchApi
      .fetch('/api/silk-infra/version')
      .then(r => r.json())
      .then((d: VersionResponse) => {
        setBuild(d.build ?? d.version ?? 'unknown');
        setDeployment(d.deployment ?? null);
        setCatalog(d.catalog?.digest ?? d.catalogVersion ?? null);
      })
      .catch(() => {});
  }, [fetchApi]);

  if (!isOpen) {
    return (
      <div className={classes.wrap}>
        <img
          src="/silk-logo-pink-box.svg"
          alt="Silk"
          className={classes.silkLogoSmall}
        />
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      <span className={classes.poweredBy}>powered by</span>
      <img
        src="/silk-logo-pink-box.svg"
        alt="Silk"
        className={classes.silkLogo}
      />
      <span className={classes.version}>build {build}</span>
      {deployment && (
        <span className={classes.version}>deploy {deployment}</span>
      )}
      {catalog && <span className={classes.version}>catalog {catalog}</span>}
    </div>
  );
};
